/**
 * Checklist Response Drawer Component
 * 
 * Drawer for Process Owner to respond to checklist questions
 * Features:
 * - Read-only question display
 * - Significant Change dropdown (Change Identified / No Changes / N/A)
 * - Response text area
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Select } from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../../../components/common/ButtonGroup';
import { Button } from 'antd';
import type { ChecklistItem, SignificantChangeStatus } from '../../../../../services/certificationService';

const { TextArea } = Input;

// ========== TYPE DEFINITIONS ==========

export interface ChecklistResponseFormData {
  checklistLineItemId: string;
  significantChange: SignificantChangeStatus | null;
  response: string;
}

export interface ChecklistResponseDrawerProps {
  open: boolean;
  item: ChecklistItem | null;
  onClose: () => void;
  onSubmit: (data: ChecklistResponseFormData) => void;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const QuestionDisplay = styled.div`
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px;
  font-family: Inter;
  font-size: 14px;
  line-height: 1.6;
  color: #1F2937;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    height: 44px !important;
    border-radius: 8px !important;
    align-items: center;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #1F2937;
  }

  .ant-select-selection-item {
    line-height: 42px !important;
  }
`;

const StyledTextArea = styled(TextArea)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #1F2937;
  min-height: 150px !important;

  &.ant-input {
    color: #1F2937;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

const SignificantChangeOption = styled.div<{ $status: SignificantChangeStatus }>`
  display: flex;
  align-items: center;
  gap: 8px;
  
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $status }) => {
      switch ($status) {
        case 'changeIdentified':
          return '#DC2626';
        case 'noChanges':
          return '#2563EB';
        case 'notApplicable':
          return '#9CA3AF';
        default:
          return '#9CA3AF';
      }
    }};
  }
`;

// ========== MAIN COMPONENT ==========

const ChecklistResponseDrawer: React.FC<ChecklistResponseDrawerProps> = ({
  open,
  item,
  onClose,
  onSubmit,
}) => {
  // Form states
  const [form] = Form.useForm();
  const [significantChange, setSignificantChange] = useState<SignificantChangeStatus | null>(null);
  const [response, setResponse] = useState('');

  // Reset form when drawer opens/closes or item changes
  useEffect(() => {
    if (open && item) {
      setSignificantChange(item.significantChange);
      setResponse(item.response || '');
      form.setFieldsValue({
        significantChange: item.significantChange,
        response: item.response,
      });
    }
  }, [open, item, form]);

  const resetForm = () => {
    setSignificantChange(null);
    setResponse('');
    form.resetFields();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFormSubmit = () => {
    if (!item) return;
    
    onSubmit({
      checklistLineItemId: item.checklistLineItemId,
      significantChange,
      response,
    });
    handleClose();
  };

  // Validation: Response is required when "Change Identified" is selected
  const isResponseRequired = significantChange === 'changeIdentified';
  const isResponseDisabled = significantChange !== null && significantChange !== 'changeIdentified';
  const isResponseMissing = isResponseRequired && !response.trim();
  const canSubmit = !isResponseMissing;

  // Significant change options
  const significantChangeOptions = [
    {
      value: 'changeIdentified' as SignificantChangeStatus,
      label: (
        <SignificantChangeOption $status="changeIdentified">
          <span className="dot" />
          Change Identified
        </SignificantChangeOption>
      ),
    },
    {
      value: 'noChanges' as SignificantChangeStatus,
      label: (
        <SignificantChangeOption $status="noChanges">
          <span className="dot" />
          No Changes
        </SignificantChangeOption>
      ),
    },
    {
      value: 'notApplicable' as SignificantChangeStatus,
      label: (
        <SignificantChangeOption $status="notApplicable">
          <span className="dot" />
          N/A
        </SignificantChangeOption>
      ),
    },
  ];

  return (
    <RightContentDrawer
      open={open}
      title="Respond to Question"
      width={572}
      onClose={handleClose}
      footer={
        <ButtonGroup>
          <Button
            type="default"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleFormSubmit}
            disabled={!canSubmit}
          >
            Save Response
          </Button>
        </ButtonGroup>
      }
    >
      <StyledForm
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        {/* Question (Read-only) */}
        <Form.Item>
          <div>
            <FormLabel>Question</FormLabel>
            <QuestionDisplay>
              {item?.question || '-'}
            </QuestionDisplay>
          </div>
        </Form.Item>

        {/* Significant Change */}
        <Form.Item name="significantChange">
          <div>
            <FormLabel>Significant Change</FormLabel>
            <StyledSelect
              value={significantChange}
              onChange={(value) => setSignificantChange(value as SignificantChangeStatus)}
              options={significantChangeOptions}
              placeholder="Select significant change status..."
            />
            <InfoText>
              Select whether there have been any significant changes related to this question.
            </InfoText>
          </div>
        </Form.Item>

        {/* Response */}
        <Form.Item name="response">
          <div>
            <FormLabel>
              Response
              {isResponseRequired && <span style={{ color: '#DC2626', marginLeft: 4 }}>*</span>}
            </FormLabel>
            <StyledTextArea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder={isResponseDisabled ? '' : 'Enter your response...'}
              rows={6}
              spellCheck={true}
              disabled={isResponseDisabled}
              status={isResponseMissing ? 'error' : undefined}
            />
            <InfoText style={isResponseMissing ? { color: '#DC2626' } : undefined}>
              {isResponseMissing 
                ? 'Response is required when "Change Identified" is selected.'
                : 'Provide additional details or explanation for your answer.'
              }
            </InfoText>
          </div>
        </Form.Item>
      </StyledForm>
    </RightContentDrawer>
  );
};

export default ChecklistResponseDrawer;

